import React from "react";
import { FieldProps } from "formik";
import { Avatar, Box, IconButton } from "@material-ui/core";
import BrowseButton from "./BrowseButton";
import Typography from "./Typography";
import { ReactComponent as RedTrashIcon } from "assets/icon/red_trash.svg";
import { compose, withTranslation, withHooks } from "enhancers";
import { TFunction } from "i18next";
import { AppColor } from "theme/app-color";

interface FileUploadWithPreviewProps extends FieldProps<any> {
  t: TFunction;
  label?: string;
  addButtonLabel?: string;
  description?: string;
  enabled?: boolean;
  disabled?: boolean;
  accept?: string | string[];
  helperText?: string;
}

const FileUploadWithPreview = ({
  t,
  label,
  addButtonLabel,
  description,
  accept,
  isEnabled,
  hasFile,
  fileUrl,
  fileName,
  addDocument,
  removeDocument,
}: any) => {
  return (
    <Box display="flex" flexDirection="column" style={{ marginTop: "16px" }}>
      {label && <Typography variant="Helper/12">{label}</Typography>}
      {isEnabled && (
        <BrowseButton
          onBrowse={addDocument}
          accept={accept}
          multiple={false}
          style={{
            width: "100%",
            marginTop: "8px",
            backgroundColor: AppColor["Upload/Background"],
            border: `1px dashed ${AppColor["Upload/Border"]}`,
            height: "40px",
          }}
        >
          <Typography variant="caption" color={AppColor["Text/Primary"]}>
            {addButtonLabel || t(".uploadFile")}
          </Typography>
        </BrowseButton>
      )}

      {hasFile && (
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          style={{
            padding: "8px",
            border: `1px solid ${AppColor["Upload/Border"]}`,
            borderRadius: "8px",
            marginTop: "8px",
          }}
        >
          <Box display="flex" alignItems="center">
            {fileUrl && (
              <Avatar
                variant="square"
                style={{ height: "48px", width: "48px" }}
                src={fileUrl}
              />
            )}

            <Typography variant="caption" noWrap style={{ marginLeft: "8px" }}>
              {fileName || "ไฟล์ที่กำลังรออัปโหลด"}
            </Typography>
          </Box>

          {isEnabled && (
            <IconButton
              size="small"
              onClick={() => {
                removeDocument();
              }}
            >
              <RedTrashIcon width={18} height={18} />
            </IconButton>
          )}
        </Box>
      )}
      {description && (
        <Typography
          variant="caption"
          color={AppColor["Text/Line"]}
          style={{ marginTop: "4px" }}
        >
          {description}
        </Typography>
      )}
    </Box>
  );
};

const enhancer = compose(
  withTranslation({ prefix: "components.advance.SingleFileUpload" }),
  withHooks((props: FileUploadWithPreviewProps, hooks: any) => {
    const {
      field,
      form,
      t,
      label,
      addButtonLabel,
      description,
      enabled = true,
      disabled = false,
      accept = "image/*",
    } = props;
    const { useCallback, useMemo } = hooks;

    const isEnabled = enabled !== false && !disabled;
    const { value } = field;

    const addDocument = useCallback(
      (file: any) => {
        form.setFieldValue(field.name, file);
      },
      [form, field.name]
    );

    const removeDocument = useCallback(() => {
      form.setFieldValue(field.name, null);
    }, [form, field.name]);

    const hasFile = !!value;

    const fileName = useMemo(() => {
      if (!value) {
        return "";
      }

      return value?.name || value.fileName || "";
    }, [value]);

    const fileUrl = useMemo(() => {
      if (!value) {
        return "";
      }

      let url = "";
      if (value instanceof File) {
        url = URL.createObjectURL(value);
      } else if (value.url) {
        url = value.url;
      }

      return url;
    }, [value]);

    return {
      t,
      label,
      addButtonLabel,
      description: description || props.helperText,
      accept,
      isEnabled,
      hasFile,
      fileUrl,
      fileName,
      addDocument,
      removeDocument,
    };
  })
);

export default enhancer(FileUploadWithPreview);
